import type { InputHTMLAttributes, LabelHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import { cn } from '../../lib/cn';

const CONTROL =
  'w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 transition-colors duration-150 focus:border-accent disabled:cursor-not-allowed disabled:opacity-60';

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('block text-[13px] font-medium text-foreground', className)} {...props} />;
}

export interface FieldProps {
  label: string;
  /** Rendered under the control; kept visible rather than hidden in a placeholder. */
  hint?: string;
  error?: string | undefined;
  required?: boolean;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
}

/** Label + control + hint/error, wired up with the aria attributes screen readers need. */
export function Field({ label, hint, error, required, children, className }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {error ? (
        <p id={errorId} className="text-[13px] text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-[13px] text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function Input({ className, invalid, ...props }: InputProps) {
  return <input className={cn(CONTROL, 'h-9', invalid && 'border-destructive', className)} aria-invalid={invalid} {...props} />;
}

export function Textarea({ className, invalid, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return <textarea className={cn(CONTROL, 'min-h-20 py-2', invalid && 'border-destructive', className)} aria-invalid={invalid} {...props} />;
}

export function Select({ className, invalid, children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return (
    <select className={cn(CONTROL, 'h-9 pr-8', invalid && 'border-destructive', className)} aria-invalid={invalid} {...props}>
      {children}
    </select>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
}

export function Checkbox({ className, label, id, ...props }: CheckboxProps) {
  const generated = useId();
  const controlId = id ?? generated;
  return (
    <span className="inline-flex items-center gap-2">
      <input
        id={controlId}
        type="checkbox"
        className={cn('h-4 w-4 rounded border-border-strong text-accent accent-[var(--accent)]', className)}
        {...props}
      />
      {label ? (
        <label htmlFor={controlId} className="text-[13px] text-foreground">
          {label}
        </label>
      ) : null}
    </span>
  );
}
